import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// SERVICES
import { RefreshService } from 'src/app/refresh.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';

// CONSTANTS
import {
  RDPD_TABLE_SUB_HEADERS,
  DISTRIBUTOR_TYPE_OPTIONS,
  STATUS_OPTIONS,
} from 'src/app/lib/constants/rdpd.constants';

// VALUE OBJECTS
import * as vo from 'src/app/core/model/server/vo/_index';

// COMPONENTS (FOR MODAL)
import { ViewDistributorsComponent } from './view-distributors/view-distributors.component';
import { AddDistributorComponent, DistributorTypeData } from './add-distributor/add-distributor.component';
import { rdpdService } from './rdpd.service';

@Component({
  selector: 'app-features-rdpd-dashboard',
  templateUrl: './features-rdpd-dashboard.component.html',
  styleUrls: ['./features-rdpd-dashboard.component.scss'],
})
export class FeaturesRdpdDashboardComponent implements OnInit, OnDestroy {
  showTable = true;
  private destroy$ = new Subject<void>();

  public form!: UntypedFormGroup;
  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;

  // ─── Dropdown Options ──────────────────────────────────────────────────────

  statusOptions          = STATUS_OPTIONS;
  distributorTypeOptions = DISTRIBUTOR_TYPE_OPTIONS;

  // ─── Dropdown State ────────────────────────────────────────────────────────

  statusType: any       = null;
  statusType_text: any  = null;
  distributorType: any      = null;
  distributorType_text: any = null;

  // ─── Table ─────────────────────────────────────────────────────────────────

  tableSubHeader = RDPD_TABLE_SUB_HEADERS;

  // ─── Data ──────────────────────────────────────────────────────────────────

  itemLists: vo.IDistributor[]               = [];
  submitted                                  = false;
  isLoading                                  = false;
  fileName                                   = '';
  filterFields: vo.GetAllDistributorsRequest | null = null;

  // ─── Pagination ────────────────────────────────────────────────────────────

  size                         = 10;
  total_pages                  = 0;
  current_page                 = 1;
  pages_array: number[] | null = [];
  total                        = 0;

  // ─── Search & Limit ────────────────────────────────────────────────────────

  search_keyword = '';
  searchKey: any = null;
  limitKey: any  = null;

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sort_column               = 'dateCreated';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Constructor ───────────────────────────────────────────────────────────

  constructor(
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private refreshService: RefreshService,
    private dialog: MatDialog,
    private rdpdFacade: rdpdService,
    private popUpDialog: PopUpDialogService,
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.spinner.hide().catch((err) => console.error('Spinner hide error on init:', err));

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resetTableState();
      this.showTable = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.spinner.hide().catch((err) => console.error('Spinner hide error on destroy:', err));
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.form = this.fb.group({
      firstName:   [null],
      middleName:  [null],
      lastName:    [null],
      phoneNumber: [null],
      assignedBy:  [null], 
      dateFrom:    [null], 
      dateTo:      [null], 
    });

    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit: [this.size] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.searchForm.value.search;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.limitForm.value.limit;
      if (this.filterFields) { this.get_filtered_list(); }
    }, 500);
  }

  // ─── Build Request Payload ────────────────────────────────────────────────

  private buildRequestParams(includeSearch = false): vo.GetAllDistributorsRequest {
    const params: vo.GetAllDistributorsRequest = {
      firstName:       this.form.value.firstName   || null,
      middleName:      this.form.value.middleName  || null,
      lastName:        this.form.value.lastName    || null,
      phoneNumber:     this.form.value.phoneNumber || null,
      assignedBy:      this.form.value.assignedBy  || null,
      dateFrom:        this.form.value.dateFrom    || null,
      dateTo:          this.form.value.dateTo      || null,
      distributorType: this.distributorType            || null,  
      status: this.statusType !== null ? this.statusType : null,
      sortBy:          { [this.sort_column]: this.sort_type },
    };

    if (includeSearch && this.search_keyword) {
      params.search = this.search_keyword;
    }

    return params;
  }

  // ─── Submit ────────────────────────────────────────────────────────────────

  submit(): void {
    this.isLoading    = true;
    this.current_page = 1;
    this.pages_array  = [];
    this.showTable    = true;

    const params      = this.buildRequestParams();
    this.filterFields = params;
    localStorage.setItem('rdpdList', JSON.stringify(params));

    this.spinner.show().catch((err) => console.error(err));

    this.rdpdFacade
      .getAllDistributors(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.handleListResponse(res);
          this.submitted = true;
          this.form.reset();
          this.resetDropdowns();
        },
        error: (err) => {
          this.toastr.error('Failed to fetch distributor list.');
          this.isLoading = false;
          this.spinner.hide().catch((e) => console.error(e));
        },
      });
  }

  // ─── Filtered List ─────────────────────────────────────────────────────────

  get_filtered_list(): void {
    this.isLoading   = true;
    this.pages_array = [];

    const params: vo.GetAllDistributorsRequest = {
      ...this.filterFields,
      search: this.search_keyword || null,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    localStorage.setItem('rdpdList', JSON.stringify(params));

    this.rdpdFacade
      .getAllDistributors(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next:  (res) => this.handleListResponse(res),
        error: (err) => {
          this.toastr.error('Failed to fetch distributor list.');
          this.isLoading = false;
          this.spinner.hide().catch((e) => console.error(e));
        },
      });
  }

  // ─── Toggle Status ─────────────────────────────────────────────────────────
  toggleStatusConfirmation(item: vo.IDistributor): void {
    const enabling = item.status === 'Disabled';
    const action   = enabling ? 'enable' : 'disable';

    this.popUpDialog.openStatusActionDialog(
      `Are you sure you want to ${action} this account?`,
      action.toUpperCase(),
      'CANCEL',
      () => this.executeToggleStatus(item, enabling),
      () => console.log(`${action} cancelled`)
    );
  }

  private executeToggleStatus(item: vo.IDistributor, enabling: boolean): void {
    this.spinner.show().catch(console.error);

    const request: vo.DistributorDoActionRequest = {
      accountGuid: item.accountGuid,
      action:      enabling ? 'enable' : 'disable',
    };

    this.rdpdFacade.doAction(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success(
              `Account has been successfully ${enabling ? 'enabled' : 'disabled'}.`
            );
            this.get_filtered_list();
          } else {
            this.popUpDialog.openErrorDialog(
              `Failed to ${enabling ? 'enable' : 'disable'} account. Please try again.`
            );
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog('Something went wrong. Please try again later.');
          this.spinner.hide().catch(console.error);
        },
        complete: () => {
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Remove ────────────────────────────────────────────────────────────────

  removeDistributorConfirmation(item: vo.IDistributor): void {
    this.popUpDialog.openStatusActionDialog(
      '<strong>Warning!</strong> <br> Removing this chosen account will result to reverting <br>back to Full (Verified) Tier. Do you wish to proceed?',
      'REMOVE',
      'CANCEL',
      () => this.executeRemove(item),
      () => console.log('remove cancelled')
    );
  }

  private executeRemove(item: vo.IDistributor): void {
    this.spinner.show().catch(console.error);

    const request: vo.RemoveDistributorRequest = {
      accountGuid: item.accountGuid,
    };

    this.rdpdFacade.removeDistributor(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 200) {
            this.toastr.success('Account successfully removed as a Distributor.');
            this.get_filtered_list();
          } else {
            this.popUpDialog.openErrorDialog('Failed to remove account. Please try again.');
          }
        },
        error: (err) => {
          this.popUpDialog.openErrorDialog('Something went wrong. Please try again later.');
          this.spinner.hide().catch(console.error);
        },
        complete: () => {
          this.spinner.hide().catch(console.error);
        },
      });
  }

  // ─── Response Handler ──────────────────────────────────────────────────────

  private handleListResponse(res: vo.GetAllDistributorsResponse): void {
    if (res?.result) {
      this.itemLists    = res.result.data         ?? [];
      this.total        = res.result.totalItems   ?? 0;
      this.total_pages  = res.result.totalPages   ?? 0;
      this.current_page = res.result.currentPage  ?? this.current_page;
      this.pages_array  = this.paginationHelper.generatePageArray(this.current_page, this.total_pages);
    } else {
      this.itemLists = [];
    }
    this.isLoading = false;
    this.spinner.hide().catch((err) => console.error(err));
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  print(): void {
    this.spinner.show().catch((err) => console.error(err));

    const params = JSON.parse(localStorage.getItem('rdpdList') ?? 'null') as vo.ExportDistributorsRequest;

    try {
      this.rdpdFacade.exportRDPD(params);
    } catch (err) {
      throw err;
    } finally {
      this.spinner.hide().catch((e) => console.error(e));
    }
  }

  // ─── Dropdown Setters ──────────────────────────────────────────────────────

  setStatusType(text: string, value: number): void {
    this.statusType      = value;
    this.statusType_text = text;
  }

  setDistributorType(text: string, value: string): void {
    this.distributorType      = value;
    this.distributorType_text = text;
  }

  // ─── Table Controls ────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) { this.searchList(); }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  // ─── Sorting & Pagination ──────────────────────────────────────────────────

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => console.error(err));
    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column, this.sort_type, sort_column, sort_type,
    );
    this.sort_column = column;
    this.sort_type   = type;
    if (this.submitted && this.filterFields) { this.get_filtered_list(); }
  }

  setPage(num: number): void {
    this.current_page = num;
    if (this.filterFields) { this.get_filtered_list(); }
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      if (this.filterFields) { this.get_filtered_list(); }
    }
  }

  // ─── Dialogs ───────────────────────────────────────────────────────────────

  openAddDistributorDialog(type: 'retailer' | 'regional' | 'provincial', parentGuid: string | null = null): void {
    const dialogRef = this.dialog.open(AddDistributorComponent, {
      width: '420px',
      disableClose: true,
      data: { distributorType: type, parentGuid } satisfies DistributorTypeData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$))
      .subscribe((saved: boolean) => { if (saved) { this.get_filtered_list(); } });
  }

  viewDistributors(type: 'retailer' | 'regional' | 'provincial', guid: string): void {
    this.dialog.open(ViewDistributorsComponent, {
      width: '1200px',
      maxHeight: '90vh',
      height: '90vh',
      panelClass: 'referral-view-dialog',
      data: { distributorType: type, accountGuid: guid },
    });
  }

  // ─── Resets ────────────────────────────────────────────────────────────────

  refresh(): void {
    this.resetTableState();
    this.showTable = false;
  }

  private resetDropdowns(): void {
    this.statusType       = null;
    this.statusType_text  = null;
    this.distributorType      = null;
    this.distributorType_text = null;
  }

  private resetTableState(): void {
    this.form.reset();
    this.searchForm.reset({ search: '' });
    this.limitForm.reset({ limit: this.size });
    this.resetDropdowns();
    this.itemLists      = [];
    this.submitted      = false;
    this.isLoading      = false;
    this.fileName       = '';
    this.filterFields   = null;
    this.size           = 10;
    this.total_pages    = 0;
    this.current_page   = 1;
    this.pages_array    = null;
    this.total          = 0;
    this.search_keyword = '';
    localStorage.setItem('rdpdList', JSON.stringify(null));
  }



  // ─── Admin Guard ───────────────────────────────────────────────────────────
  getAdminButtonState(): { disabled: boolean; tooltip: string } {
    if (!this.rdpdFacade.isAdmin) {
      return {
        disabled: true,
        tooltip: 'You are not allowed to change details of rdpd dashboard. Please contact an Admin if you need assistance',
      };
    }
    return { disabled: false, tooltip: '' };
  }


  // ─── Mappers ───────────────────────────────────────────────────────────
  toDistributorTypeKey(raw: string): 'retailer' | 'regional' | 'provincial' {
    const map: Record<string, 'retailer' | 'regional' | 'provincial'> = {
      Retailer: 'retailer',
      'Regional Distributor':  'regional',
      'Provincial Distributor':'provincial',
    };
    return map[raw] ?? 'retailer';
  }
}
